import { pageLinksOrDefault, pageMetadataOrDefault, viewerInfoOrDefault, } from "@snapchat/content-platform-www/lib/types/fallbacks";
import { PageType } from "@snapchat/web-blizzard-reporting";
import { useWebSession } from "@snapchat/web-blizzard-reporting-hooks";
import { ConsumerApp } from "@snapchat/web-consumer-nav";
import { consumerWebOneLinkParams, MobileCTAVariants, PageFrame, Shell, useMobileMediaQuery, useTabletMediaQuery, } from "@snapchat/web-frame";
import { getDirectionForLocale, useLocale } from "@snapchat/web-i18n";
import { useGenerateWebPageViewTraceId } from "@snapchat/web-page-view";
import { usePageLoadMetrics } from "@snapchat/web-performance-metrics";
import { useCreateOrUpdateWebClientId, useEssentialSession } from "@snapchat/web-sc-cookies";
import { incrementCounter } from "@snapchat/web-graphene-reporting";
import clsx from "clsx";
import { useEffect, useMemo } from "react";
import { LENS_CATEGORY } from "../../../constants/categories";
import { grafanaBasePrefix } from "../../../lib/grafana";
import { CategoriesBanner } from "./CategoriesBanner";
import styles from "./LensHomePage.module.css";
import { TopLensesGrid } from "./TopLensesGrid";
import { getBaseShellProps } from "../../../lib/shell";
import { LENS_DATA_SOURCE_NAME, LENS_ORIGIN } from "../../../lib/environment";
import { loadMessages } from "../../../i18n/messages";
import { ExperimentId } from "../../../lib/experiment";
import { WWW_DOT_SNAPCHAT_DOT_COM_GA } from "@snapchat/web-analytics-types";
import { topLensToVideoMetadata } from "../../../lib/metadata";
function LensHomePage(props) {
    const pageName = "Lens_Home";
    const { traceId: webPageViewTraceId } = useGenerateWebPageViewTraceId();
    const webClientId = useCreateOrUpdateWebClientId({
        isGdpr: props.viewerInfo?.isGdprCountry,
    });
    useEffect(() => {
        void incrementCounter(`${grafanaBasePrefix}.${pageName}`, 1);
    }, []);
    const { locale, messages, changeLocale } = useLocale({
        defaultLocale: props.viewerInfo?.locale,
        defaultMessages: props.messages,
        loadMessages,
    });
    const pageType = PageType.LENS_HOME_PAGE;
    const pageMetadata = pageMetadataOrDefault(props.pageMetadata);
    const viewerInfo = viewerInfoOrDefault(props.viewerInfo);
    const linkPreview = props.linkPreview;
    const pageLinks = pageLinksOrDefault(props.pageLinks);
    useEssentialSession();
    const isMobile = useMobileMediaQuery();
    const isTablet = useTabletMediaQuery();
    const webVitalsRef = usePageLoadMetrics(pageType.toString(), grafanaBasePrefix);
    useWebSession(pageType, webVitalsRef, webClientId);
    const oneLinkParams = consumerWebOneLinkParams(PageType[PageType.LENS_HOME_PAGE].toLowerCase(), "https://link.snapchat.com/lens_explorer/category?category_id=0&sc_referrer=web", "https://apps.apple.com/app/apple-store/id447188370?pt=614006&ct=lens_home_page&mt=8", "https://snapchat.com/download?purpose=web_stories&sp=lens_home_page");
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const categories = props.categories;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const selectedCategory = categories
        ? categories.find((category) => category.isSelected)
        : undefined;
    const categoriesSelected = !isMobile || selectedCategory?.name === LENS_CATEGORY.WEB_LIVE
        ? categories
        : categories.filter((category) => category.name !== LENS_CATEGORY.WEB_LIVE);
    const baseShellProps = useMemo(() => getBaseShellProps(), []);
    const footerProps = {
        openInSnapchatLink: pageLinks.oneLinkUrl,
        localeChange: changeLocale,
        oneLinkParams: oneLinkParams,
        webClientId: webClientId,
        pageType: pageType,
        locale: locale,
        downloadUrl: pageLinks.downloadUrl,
        webPageViewTraceId: webPageViewTraceId,
        mobileVariant: isMobile ? MobileCTAVariants.EXPANDED_TRAY_WITH_OVERLAY : undefined,
        isMobile,
    };
    const newOISExperimentProps = {
        enableNewOISExperiment: true,
        configID: ExperimentId.NEW_MOBILE_OIS_CTA_DESIGN,
        newFooterProps: {
            webClientId: webClientId ?? "",
            webPageViewTraceId,
            pageType,
            attribution: {
                oneLinkParams,
                openInSnapchatLink: pageLinks.oneLinkUrl,
                downloadLink: pageLinks.downloadUrl,
            },
        },
    };
    return (<Shell isGDPRCountry={viewerInfo.isGdprCountry} locale={locale} messages={messages} country={viewerInfo.country} mountByDefault canonicalUrl={pageLinks.canonicalUrl} hrefLangTagOptions={{
            template: `${pageLinks.canonicalUrl}?locale={locale}`,
            defaultValue: pageLinks.canonicalUrl,
        }} direction={getDirectionForLocale(locale)} description={pageMetadata.pageDescription?.value} snapchatDeepLinkUrl={pageLinks.snapchatDeepLinkUrl} linkPreview={linkPreview} title={pageMetadata.pageTitle} gaid={viewerInfo.isGdprCountry ? undefined : WWW_DOT_SNAPCHAT_DOT_COM_GA} pageMetadata={{
            pageType,
        }} webClientId={webClientId} webPageViewTraceId={webPageViewTraceId} sentryOrigin={LENS_ORIGIN} sentryDsnUri={LENS_DATA_SOURCE_NAME} skipWebConfigNamespaceProviderInitialization={props.skipWebConfigNamespaceProviderInitialization} videoMetadata={props.topLenses.map(topLensToVideoMetadata)} {...baseShellProps}>
      <PageFrame locale={locale} localeChange={changeLocale} appType={ConsumerApp.Lens} pageType={pageMetadata.pageType} downloadUrl={pageLinks.downloadUrl} webClientId={webClientId} webPageViewTraceId={webPageViewTraceId} footerProps={footerProps} newOISExperimentProps={newOISExperimentProps} disableUpsellQRCode={props.disableUpsellQRCode}>
        <>
          <div className={clsx(styles.categoriesBanner, {
            [styles.mobileCategoriesBanner]: isMobile,
        })} data-testid="categoriesBanner">
            <CategoriesBanner pageName={pageName} categories={categoriesSelected} isTablet={isTablet} isMobile={isMobile} basePath={props.basePath}/>
          </div>
          <TopLensesGrid topLenses={props.topLenses} isMobile={props.isMobile} hasMore={props.hasMore} nextCursorId={props.nextCursorId} category={selectedCategory} creatorDisplayInfos={props.creatorDisplayInfos} pageName={pageName} enableLargePreview={false} enableVideoPreview={!isMobile} basePath={props.basePath}/>
        </>
      </PageFrame>
    </Shell>);
}
export default LensHomePage;
